import { Component, inject, signal, ChangeDetectionStrategy, effect } from '@angular/core';
import { UsersGroupsControllerComponent } from '../../../../../shared/users-groups-controller/users-groups-controller.component';
import { FormGroup, FormBuilder, FormControl, Validators } from '@angular/forms';
import { InstructionBuilderService } from '../services/instruction-builder.service';
import { MarketplaceController } from "../../../../../shared/marketplace-controller/marketplace-controller";
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';
import { ApiService } from '../../../../../services/api.service';
import { ConversationTypeEnum } from '../../../../../pages/conversation-starter/conversation-type-enum';
import {
  PendingAccessUsersDialogComponent
} from '../../../../../shared/pending-access-users-dialog/pending-access-users-dialog.component';
import { INotifiedUser } from '../../../../../interfaces/i-notification';
import { IUser } from '../../../../../interfaces/i-user-profile';

@Component({
  selector: 'app-instruction-security',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    UsersGroupsControllerComponent,
    MarketplaceController,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './instruction-security.component.html',
  styleUrl: './instruction-security.component.scss'
})
export class InstructionSecurityComponent {
  instructionForm!: FormGroup;

  /** Users who have requested access and are awaiting approval. */
  readonly pendingUsers = signal<INotifiedUser[]>([]);
  /** True while the pending-users API call is in flight. */
  readonly pendingUsersLoading = signal(false);

  #formBuilder = inject(FormBuilder);
  #instructionBuilderService = inject(InstructionBuilderService);
  #apiService = inject(ApiService);
  #dialog = inject(MatDialog);

  /** Track the last instruction id we fetched pending users for to avoid duplicate calls. */
  #lastFetchedId = '';

  constructor() {
    this.instructionForm = this.#formBuilder.group({
      owners: new FormControl([], [Validators.required]),
      users: new FormControl([]),
      groups: new FormControl([]),
      isAdvertised: new FormControl(false),
      isAutoApproved: new FormControl(false),
      notifyAutoApproval: new FormControl(false)
    });

    // Seed owners once from the current snapshot so the child UsersGroupsController
    // can display the existing owners immediately. We do NOT patch owners inside the
    // effect below because UsersGroupsController is authoritative for owner changes.
    const snapshot = this.#instructionBuilderService.instruction();
    const snapshotOwners = snapshot?.details?.owners ?? [];
    if (snapshotOwners.length > 0) {
      this.instructionForm.get('owners')?.setValue(snapshotOwners, { emitEvent: false });
    }

    // React to the instruction signal to keep non-owner fields and the
    // pending-users call in sync, regardless of whether the user navigated
    // directly to this step or arrived via the stepper.
    effect(() => {
      const instruction = this.#instructionBuilderService.instruction();
      const details = instruction?.details;

      // Deliberately exclude 'owners' — the child UsersGroupsController owns that
      // control and writes back via onOwnersChanged(). Patching it here would
      // overwrite the user's selection every time the signal updates.
      this.instructionForm.patchValue({
        users: details?.users ?? [],
        groups: details?.groups ?? [],
        isAdvertised: details?.isAdvertised ?? false,
        isAutoApproved: details?.isAutoApproved ?? false,
        notifyAutoApproval: details?.notifyAutoApproval ?? false
      }, { emitEvent: false });

      // Re-emit isAdvertised so MarketplaceController picks up the API-provided value
      // (we use emitEvent:false above to avoid overwriting owners which are controlled
      // by the UsersGroupsController child component).
      const advControl = this.instructionForm.get('isAdvertised');
      if (advControl) {
        advControl.setValue(advControl.value, { emitEvent: true });
      }

      const instructionId = instruction?.id;
      if (instructionId && instructionId !== this.#lastFetchedId) {
        this.#lastFetchedId = instructionId;
        // Seed owners from the newly-loaded instruction (edit/clone scenario).
        if (details?.owners?.length) {
          this.instructionForm.get('owners')?.setValue(details.owners, { emitEvent: false });
        }
        this.pendingUsersLoading.set(true);
        this.#apiService.getPendingUsers(instructionId, ConversationTypeEnum.Instruction).subscribe({
          next: (users: INotifiedUser[]) => this.pendingUsers.set(users ?? []),
          error: () => this.pendingUsers.set([]),
          complete: () => this.pendingUsersLoading.set(false)
        });
      }
    });
  }

  /**
   * Opens the pending access users dialog.
   * Any users confirmed in the dialog are merged into the `users` form control.
   */
  openPendingUsersDialog(): void {
    const dialogRef = this.#dialog.open(PendingAccessUsersDialogComponent, {
      data: { pendingUsers: this.pendingUsers() }
    });

    dialogRef.afterClosed().subscribe((selectedUsers: IUser[] | undefined) => {
      if (!selectedUsers?.length) return;
      const current: IUser[] = this.instructionForm.get('users')?.value ?? [];
      const merged = [
        ...current,
        ...selectedUsers.filter(s => !current.some(c => c.userId === s.userId))
      ];
      this.instructionForm.get('users')?.setValue(merged);
      this.updateInstruction();
    });
  }

  /**
   * Validates the form and returns whether it's valid or not.
   */
  isValid(): boolean {
    if (!this.instructionForm.valid) {
      Object.values(this.instructionForm.controls).forEach(control => {
        control.markAsTouched();
        control.updateValueAndValidity();
      });
      return false;
    }
    return this.instructionForm.valid;
  }

  /**
   * Marks all controls in the security form as touched.
   */
  markFormAsTouched(): void {
    this.instructionForm.markAllAsTouched();
  }

  /**
   * Resets the security form to its initial state.
   */
  resetForm() {
    this.instructionForm.reset();
    this.instructionForm.markAsPristine();
    this.instructionForm.markAsUntouched();
  }

  /**
   * Updates the instruction service with the current values from the security form.
   */
  updateInstruction() {
    const currentInstruction = this.#instructionBuilderService.instruction();
    const updatedInstruction = {
      ...currentInstruction,
      details: {
        ...currentInstruction.details,
        ...this.instructionForm.value
      }
    };
    this.#instructionBuilderService.instruction.set(updatedInstruction);
  }

  /**
   * Handler for the ownersChanged event emitted by the UsersGroupsControllerComponent.
   */
  onOwnersChanged() {
    this.updateInstruction();
  }
}
